import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, debugAssert} from 'Asserts';
import {syncerConsoleDebug} from 'graphql-client/sync/SyncerConsoleDebug';
import {AsymmetricModelSyncer} from 'graphql-client/sync/SyncerFactory';
import {
  reportInputSize,
  startApolloSyncPerf,
  startReduxSyncPerf,
  SyncerPerfData,
} from 'graphql-client/sync/SyncerMetrics';
import * as Perf from 'performance/Perf';

import FragmentWatcherLink, {FragmentResult} from './FragmentWatcherLink';
import SYNC_DEFINITIONS from './SyncDefinitions';
import Syncer from './Syncer';
import {SyncInterface, TFragmentName, TFragmentSyncer} from './SyncInterface';

// Exported for tests
export function syncFragmentsIntoExternalStores(fragResults: Array<FragmentResult>): Array<string> {
  // We want to batch these for a few reasons:
  //   - Performance (it's better to dispatch a single action with many objects, rather than many actions,
  //     since each dispatch can trigger a re-render)
  //   - Some components may rely on the invariant that if a store's data is non-null, then all requested
  //     items are present.

  const updatedFragNames: Array<string> = [];
  const fragResultsByName = _.groupBy(fragResults, (result) => result.fragmentName);
  Object.entries(fragResultsByName).forEach(([fragName, fragResultsWithName]) => {
    updatedFragNames.push(fragName);
    const syncer = SYNC_DEFINITIONS[fragName as keyof SyncInterface];
    if (syncer != null) {
      const syncerName = getSyncerName(syncer);
      const syncPerfId: Perf.PerfId = startApolloSyncPerf(syncerName);
      syncerConsoleDebug(`Writing Apollo->Redux for syncer ${syncerName}`, fragResultsWithName);
      Perf.measureFn(syncPerfId, () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        syncer.writeToStore(fragResultsWithName.map((result) => result.data) as any);
      });
      Perf.stop(syncPerfId);
    }
  });
  return updatedFragNames;
}

function getSyncerName(syncer: TFragmentSyncer<TFragmentName>): string {
  // @ts-expect-error Declaration name exists in most syncers except the manually created ones
  return syncer.declarationName ?? syncer.constructor.name;
}

export class SyncManager {
  public fragmentWatcherLink: FragmentWatcherLink;
  private isUpdatingExternalStores = false;

  // Cache the last return value of syncer.readFromStore(); if it's equal, don't rewrite everything to
  // Apollo.
  syncerNameToLastInputs: {[key: string]: {current: unknown; equalityRef: unknown}} = {};

  // Avoid resubscribing syncers that are proxied to avoid duplicate work
  alreadySubscribedSyncers: Array<TFragmentSyncer<TFragmentName>> = [];

  unsubscribeFuncs: Array<() => void> = [];

  constructor() {
    this.subscribeToNonGraphQLChanges();
    this.fragmentWatcherLink = new FragmentWatcherLink(this.syncFragmentsIntoExternalStores);
  }

  private syncFragmentsIntoExternalStores = (fragResults: Array<FragmentResult>): void => {
    // A "naive lock" here is safe here because:
    //   - Javascript is single-threaded, and this isn't calling anything async, so nothing can pre-empty this
    //     execution; and
    //   - nothing in the child function's call tree would ever call into this logic (this is attached to
    //     Apollo at the middleware level)
    this.isUpdatingExternalStores = true;
    const updatedFragNames = syncFragmentsIntoExternalStores(fragResults);
    this.updateReduxLastInputs(updatedFragNames);
    this.isUpdatingExternalStores = false;
  };

  private updateReduxLastInputs = (updatedFragNames: Array<string>): void => {
    updatedFragNames.forEach((fragName) => {
      const syncer = SYNC_DEFINITIONS[fragName as keyof SyncInterface];
      // AsymmetricModelSyncer doesn't have a store component
      if (syncer && !(syncer instanceof AsymmetricModelSyncer)) {
        const syncerName = getSyncerName(syncer);
        const currentStore = syncer.readFromStore();
        if (currentStore === undefined) {
          return;
        }
        this.syncerNameToLastInputs[syncerName] = currentStore;
      }
    });
  };

  private handleExternalStoreChange<TInputs extends {}, TData extends {__typename: string}>(
    syncer: Syncer<TInputs, TData>,
    lastInputs: {current: unknown; equalityRef: unknown} | undefined,
    updateLastInput: (inputs: {current: unknown; equalityRef: unknown}) => void,
    perfIds: SyncerPerfData
  ): void {
    if (this.isUpdatingExternalStores) {
      // When a query returns from the network, syncFragmentsIntoExternalStores calls individual syncers,
      // which dispatch updates to stores. Because syncers also listen to these stores,
      // handleExternalStoreChange is triggered... to sync an update that was already destined for Apollo.
      // This may end up double-rendering subscribed components (or at least take time doing a deep equality
      // check), so avoid it.
      return;
    }

    Perf.markStart(perfIds.EqualityId);
    const storeData = syncer.readFromStore();
    assert(storeData !== undefined, 'syncer.readFromStore() should not return undefined');
    const {current, equalityRef} = storeData;
    updateLastInput(storeData);
    reportInputSize(perfIds.SyncerName, current);

    const inputsChanged =
      lastInputs === undefined ||
      (!isEqual(equalityRef, lastInputs.equalityRef) && !isEqual(current, lastInputs.current));
    if (lastInputs !== undefined) {
      // current equality was the previous method and is the gold standard, so we protect against
      // cases where current doesn't match the calculated
      debugAssert(
        () => !isEqual(current, lastInputs.current) === inputsChanged,
        `InputsChanged:${inputsChanged}, which doesn't match actual equality for syncer ${
          perfIds.SyncerName
        }. ${isEqual(current, lastInputs.current)} ${isEqual(equalityRef, lastInputs.equalityRef)}`
      );
    }
    Perf.markEnd(perfIds.EqualityId);

    if (inputsChanged) {
      syncerConsoleDebug(`Writing Redux->Apollo for syncer ${perfIds.SyncerName}`, {lastInputs, storeData});
      Perf.measureFn(perfIds.WriteId, () => {
        syncer.writeToApolloCache(current);
      });
    }
  }

  unsubscribeToNonGraphQLChanges(): void {
    for (const fn of this.unsubscribeFuncs) {
      fn();
    }
  }

  subscribeToNonGraphQLChanges(): void {
    for (const fragName of Object.keys(SYNC_DEFINITIONS)) {
      const syncer = SYNC_DEFINITIONS[fragName as keyof SyncInterface];

      if (syncer != null) {
        const syncerName = getSyncerName(syncer);
        const syncerPerfData = startReduxSyncPerf(syncerName);

        if (!this.alreadySubscribedSyncers.includes(syncer)) {
          this.alreadySubscribedSyncers.push(syncer);
          this.unsubscribeFuncs.push(
            syncer.subscribe(() => {
              this.handleExternalStoreChange(
                syncer as Syncer<{}, {__typename: string}>,
                this.syncerNameToLastInputs[syncerName],
                (newInputs: {current: unknown; equalityRef: unknown}) => {
                  this.syncerNameToLastInputs[syncerName] = newInputs;
                },
                syncerPerfData
              );
            })
          );
        }
      }
    }
  }
}
